import { useState } from "react";
import { BASIS_META, LINE_GROUP_LABELS, LINE_ITEM_PRESETS } from "@shared/roi/defaults";
import {
  LINE_BASES,
  LINE_GROUPS,
  type LineBasis,
  type LineDirection,
  type LineGroup,
} from "@shared/roi/types";
import { Button } from "@client/components/Button";
import { SelectField, TextField } from "@client/components/Field";
import { InfoTip } from "@client/components/InfoTip";
import { Modal } from "@client/components/Modal";
import { CountField, MoneyField, PercentField } from "@client/components/editor/NumberFields";
import { Segmented } from "@client/components/editor/Segmented";
import { packageColorVar } from "@client/lib/packageColors";
import type { DraftAction, EditorLineItem, EditorModel } from "@client/lib/scenarioDraft";

/**
 * Costs and revenues that are not part of a package's price list.
 *
 * One table for both directions, because they are one table in the model: a
 * `direction` of COST or REVENUE over the same basis, amount and range. That is
 * what makes "an add-on we sell" and "the server we rent" the same kind of row,
 * and it is the reason the old page's four hard-coded cost fields collapse into
 * this panel.
 *
 * The basis is the field people get wrong, and a wrong basis is silently wrong —
 * the projection still draws. So every basis names its multiplier in a hint
 * under the select, and "Add" leads with presets that have the basis already set.
 */

const DIRECTION_OPTIONS = [
  { value: "COST" as const, label: "Cost", accent: "var(--roi-cost)" },
  { value: "REVENUE" as const, label: "Revenue", accent: "var(--roi-revenue)" },
];

interface Props {
  model: EditorModel;
  dispatch: (action: DraftAction) => void;
  disabled: boolean;
  /** Inside the rail's dialog, which carries the title and the card chrome. */
  bare?: boolean;
}

export function LineItemsPanel({ model, dispatch, disabled, bare = false }: Props) {
  const [adding, setAdding] = useState(false);

  // Grouped for reading, in the model's own group order, with each group's rows
  // left in `sortOrder`. An empty group is not a heading over nothing.
  const grouped = LINE_GROUPS.map((group) => ({
    group,
    items: model.lineItems.filter((item) => item.group === group),
  })).filter((entry) => entry.items.length > 0);

  return (
    <>
      <div className={`flex flex-col gap-4 ${bare ? "" : "island-shell"}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          {bare ? (
            <span />
          ) : (
            <div className="flex flex-col gap-1">
              <h2 className="text-base font-bold text-ink">Costs &amp; revenues</h2>
              <p className="max-w-prose text-sm text-ink-soft">
                Everything outside the package price list: what you build, host and staff, and
                anything extra you sell on top.
              </p>
            </div>
          )}
          <Button size="sm" disabled={disabled} onClick={() => setAdding(true)}>
            Add line
          </Button>
        </div>

        {model.lineItems.length === 0 && (
          <p className="rounded-xl border border-dashed border-line px-4 py-6 text-center text-base text-ink-soft">
            No costs or revenues yet. The packages cover per-client money; this is where the build,
            the servers and the add-ons go.
          </p>
        )}

        {grouped.map(({ group, items }) => (
          <section key={group} className="flex flex-col gap-2">
            <h3 className="text-xs font-bold uppercase tracking-wider text-ink-soft">
              {LINE_GROUP_LABELS[group]}
            </h3>
            <ul className="flex flex-col gap-3">
              {items.map((item) => (
                <LineItemRow
                  key={item.id}
                  item={item}
                  model={model}
                  disabled={disabled}
                  onPatch={(patch) => dispatch({ type: "patchLineItem", id: item.id, patch })}
                  onRemove={() => dispatch({ type: "removeLineItem", id: item.id })}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>

      <AddLineDialog
        open={adding}
        onClose={() => setAdding(false)}
        onAdd={(patch) => {
          dispatch({ type: "addLineItem", patch });
          setAdding(false);
        }}
      />
    </>
  );
}

/* --------------------------------------------------------------------- row */

function LineItemRow({
  item,
  model,
  disabled,
  onPatch,
  onRemove,
}: {
  item: EditorLineItem;
  model: EditorModel;
  disabled: boolean;
  onPatch: (patch: Partial<EditorLineItem>) => void;
  onRemove: () => void;
}) {
  const meta = BASIS_META[item.basis];
  const tone = item.direction === "REVENUE" ? "revenue" : "cost";
  const accent = item.direction === "REVENUE" ? "var(--roi-revenue)" : "var(--roi-cost)";
  const years = Array.from({ length: model.horizonYears }, (_, index) => index + 1);
  const label = item.name || "this line";

  return (
    <li
      className="flex flex-col gap-3 rounded-xl border border-line bg-chip-bg p-3"
      // The direction recolours the row, matching the bars it will feed.
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[10rem] flex-1">
          <TextField
            label="Name"
            value={item.name}
            maxLength={80}
            disabled={disabled}
            onChange={(event) => onPatch({ name: event.target.value })}
          />
        </div>
        <Segmented
          legend="Direction"
          value={item.direction}
          options={DIRECTION_OPTIONS}
          disabled={disabled}
          size="sm"
          onChange={(direction: LineDirection) => onPatch({ direction })}
        />
        <Button variant="destructive" size="sm" disabled={disabled} onClick={onRemove}>
          Remove
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <SelectField
          label="Basis"
          hint={meta.hint}
          value={item.basis}
          disabled={disabled}
          onChange={(event) => onPatch({ basis: event.target.value as LineBasis })}
        >
          {LINE_BASES.map((basis) => (
            <option key={basis} value={basis}>
              {BASIS_META[basis].label}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="Group"
          hint="Groups the cost breakdown chart splits by."
          value={item.group}
          disabled={disabled}
          onChange={(event) => onPatch({ group: event.target.value as LineGroup })}
        >
          {LINE_GROUPS.map((group) => (
            <option key={group} value={group}>
              {LINE_GROUP_LABELS[group]}
            </option>
          ))}
        </SelectField>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {meta.isPercentage ? (
          <PercentField
            label="Amount"
            tone={tone}
            value={item.amount}
            disabled={disabled}
            onChange={(amount) => onPatch({ amount })}
          />
        ) : (
          <MoneyField
            label="Amount"
            tone={tone}
            value={item.amount}
            currencyCode={model.currencyCode}
            disabled={disabled}
            onChange={(amount) => onPatch({ amount })}
          />
        )}

        {meta.usesUnitsPerClient && (
          <CountField
            label="Units per client"
            min={0}
            value={item.unitsPerClient}
            disabled={disabled}
            onChange={(unitsPerClient) => onPatch({ unitsPerClient })}
          />
        )}

        {meta.usesAttachPct && (
          <PercentField
            label="Attach rate"
            value={item.attachPct}
            disabled={disabled}
            hint="Share of the counted clients this applies to."
            onChange={(attachPct) => onPatch({ attachPct })}
          />
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <SelectField
          label="From year"
          value={item.startYear}
          disabled={disabled}
          onChange={(event) => onPatch({ startYear: Number(event.target.value) })}
        >
          {years.map((year) => (
            <option key={year} value={year}>
              Year {year}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="To year"
          value={item.endYear ?? ""}
          disabled={disabled}
          onChange={(event) =>
            onPatch({ endYear: event.target.value === "" ? null : Number(event.target.value) })
          }
        >
          <option value="">The end of the horizon</option>
          {years.map((year) => (
            <option key={year} value={year}>
              Year {year}
            </option>
          ))}
        </SelectField>
      </div>

      <fieldset disabled={disabled} className="min-w-0">
        <legend className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-ink">
          Applies to
          <span className="font-normal text-ink-soft">
            {item.packageIds.length === 0
              ? "every package"
              : `${item.packageIds.length} of ${model.packages.length}`}
          </span>
          <InfoTip label="which packages this applies to">
            {item.packageIds.length === 0
              ? `Nothing ticked, so ${label} counts the whole client base.`
              : `${label} counts only clients on the ticked packages.`}
          </InfoTip>
        </legend>
        <div className="flex flex-wrap items-center gap-1.5">
          {model.packages.map((pkg) => {
            const picked = item.packageIds.includes(pkg.id);
            return (
              <label
                key={pkg.id}
                className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                  picked ? "border-azure-deep text-ink" : "border-line text-ink-soft"
                }`}
              >
                <input
                  type="checkbox"
                  checked={picked}
                  disabled={disabled}
                  onChange={(event) =>
                    onPatch({
                      packageIds: event.target.checked
                        ? [...item.packageIds, pkg.id]
                        : item.packageIds.filter((id) => id !== pkg.id),
                    })
                  }
                  className="h-3.5 w-3.5 accent-[var(--azure-deep)]"
                />
                <span
                  aria-hidden="true"
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: packageColorVar(pkg.colorIndex) }}
                />
                {pkg.name || "Unnamed"}
              </label>
            );
          })}
        </div>
      </fieldset>

      <TextField
        label="Note"
        placeholder="Where this figure came from"
        maxLength={200}
        value={item.note ?? ""}
        disabled={disabled}
        onChange={(event) =>
          onPatch({ note: event.target.value === "" ? undefined : event.target.value })
        }
      />
    </li>
  );
}

/* ------------------------------------------------------------------ adding */

/**
 * Presets first, blank second. A blank row defaults to a fixed annual cost,
 * which is the right answer roughly never; starting from "Hosting" puts the
 * per-active-client basis in place before anyone has to understand the word.
 */
function AddLineDialog({
  open,
  onClose,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  onAdd: (patch: Partial<EditorLineItem>) => void;
}) {
  return (
    <Modal
      open={open}
      title="Add a cost or revenue"
      description="Start from something close and adjust it, or start blank."
      onClose={onClose}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
    >
      <ul className="grid gap-2 sm:grid-cols-2">
        {LINE_ITEM_PRESETS.map((preset) => {
          const basis = preset.patch.basis ?? "FIXED_PER_YEAR";
          return (
            <li key={preset.key}>
              <button
                type="button"
                onClick={() => onAdd(preset.patch)}
                className="flex w-full flex-col gap-0.5 rounded-lg border border-line bg-chip-bg px-3 py-2 text-left hover:border-azure-deep"
              >
                <span className="flex items-center gap-1.5 text-sm font-bold text-ink">
                  <span
                    aria-hidden="true"
                    className="inline-block h-2 w-2 rounded-full"
                    style={{
                      background:
                        preset.patch.direction === "REVENUE"
                          ? "var(--roi-revenue)"
                          : "var(--roi-cost)",
                    }}
                  />
                  {preset.label}
                </span>
                <span className="text-xs text-ink-soft">{BASIS_META[basis].hint}</span>
              </button>
            </li>
          );
        })}
        <li>
          <button
            type="button"
            onClick={() => onAdd({})}
            className="flex h-full w-full flex-col gap-0.5 rounded-lg border border-dashed border-line px-3 py-2 text-left hover:border-azure-deep"
          >
            <span className="text-sm font-bold text-ink">Blank line</span>
            <span className="text-xs text-ink-soft">Set the basis yourself.</span>
          </button>
        </li>
      </ul>
    </Modal>
  );
}
